import { useEffect, useState } from "react";
import { http } from "@/lib/api";

const fmt = (n) => (n ?? 0).toLocaleString("en-CA");
const COLS = [
  ["period", "Period"], ["conversations", "Conversations"], ["answers", "Answers"], ["prepared_answers", "Prepared"], ["reworded_answers", "Reworded matches"],
  ["ai_calls", "AI calls"], ["input_tokens", "Input tokens"], ["output_tokens", "Output tokens"], ["cached_tokens", "Cached tokens"],
  ["avg_tokens_per_conversation", "Avg tokens / conversation"],
];

const UsageTable = ({ rows, testid, showConversations }) => {
  const showCost = rows.length > 0 && rows[0].cost_usd !== undefined;
  return (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[720px] text-left text-[12px]" data-testid={testid}>
      <thead>
        <tr className="border-b border-hv-rule font-heading text-[10px] uppercase tracking-wider text-hv-muted">
          {COLS.filter(([k]) => showConversations || !["conversations", "avg_tokens_per_conversation"].includes(k)).map(([k, l]) => (
            <th key={k} className="py-2 pr-3 font-semibold">{l}</th>
          ))}
          {!showConversations && <th className="py-2 pr-3 font-heading font-semibold">Avg tokens / answer</th>}
          {showCost && <th className="py-2 pr-3 font-heading font-semibold">Est. cost (US$)</th>}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td className="py-3 text-hv-muted" colSpan={showConversations ? COLS.length : COLS.length - 1}>No AI usage recorded yet.</td></tr>}
        {rows.map((r) => (
          <tr key={r.period} className="border-b border-hv-rule last:border-0">
            {COLS.filter(([k]) => showConversations || !["conversations", "avg_tokens_per_conversation"].includes(k)).map(([k]) => (
              <td key={k} className={`py-2 pr-3 ${k === "period" ? "font-heading font-semibold text-hv-navy" : "tabular-nums"}`}>{k === "period" ? r[k] : fmt(r[k])}</td>
            ))}
            {!showConversations && <td className="py-2 pr-3 tabular-nums">{fmt(r.avg_tokens_per_answer)}</td>}
            {showCost && <td className="py-2 pr-3 tabular-nums" data-testid="usage-cost-cell">${Number(r.cost_usd || 0).toFixed(2)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const Block = ({ title, data, kind, by }) => (
  <div className="mt-6 first:mt-0" data-testid={`usage-${kind}`}>
    <p className="mb-2 font-heading text-[12px] font-semibold text-hv-text">
      {title} <span className="font-normal text-hv-muted">· {fmt(data.total.ai_calls)} AI calls, {fmt(data.total.input_tokens + data.total.output_tokens)} tokens in the last 90 days</span>
    </p>
    <UsageTable rows={data[by]} testid={`usage-${kind}-${by}-table`} showConversations={kind === "visitor"} />
  </div>
);

export const UsagePanel = () => {
  const [data, setData] = useState(null);
  const [by, setBy] = useState("daily");
  useEffect(() => { http.get("/admin/usage?days=90").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  return (
    <section className="border border-hv-rule bg-white p-6" data-testid="panel-ai-usage">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hv-rule pb-3">
        <h2 className="font-heading text-[14px] font-semibold text-hv-text">
          AI usage {data && <span className="font-normal text-hv-muted">· main model {data.model}</span>}
        </h2>
        <div className="flex" role="group" aria-label="Usage period">
          {["daily", "monthly"].map((p) => (
            <button key={p} type="button" onClick={() => setBy(p)} aria-pressed={by === p} data-testid={`usage-${p}-button`}
              className={`border border-hv-navy px-3 py-1 font-heading text-[11px] font-semibold uppercase tracking-wider ${by === p ? "bg-hv-navy text-white" : "text-hv-navy"}`}>
              {p === "daily" ? "Per day" : "Per month"}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4">
        {data === null && <p className="text-[13px] text-hv-muted">Loading…</p>}
        {data === false && <p className="text-[13px] text-hv-muted">Usage data is unavailable.</p>}
        {data && (
          <>
            <Block title="Visitor conversations" data={data.visitor} kind="visitor" by={by} />
            <Block title="Warm-up (prepared answers after each refresh)" data={data.warmup} kind="warmup" by={by} />
            <p className="mt-4 text-[11px] text-hv-muted">Prepared answers are replayed without an AI call. Real usage only; sample analytics data is not included.</p>
          </>
        )}
      </div>
    </section>
  );
};
